import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/context/LanguageContext";
import axios from "axios";
import { toast } from "sonner";
import { 
  LayoutDashboard, Users, Calendar, CreditCard, Bell, LogOut, 
  Menu, X, Search, Save, Globe, RefreshCw, FileText, Edit3, BarChart3,
  Languages, ChevronDown, ChevronRight
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import AdminSidebar from "@/components/AdminSidebar";
import { useNotificationCount } from "@/hooks/useNotificationCount";

const API = "/api";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_a07fe825-978d-42af-ad0d-66b05bf66e9f/artifacts/6h7nm3ap_Photoroom_20260215_225825.png";

const CATEGORIES = [
  { id: "common", label: "Common", icon: Globe },
  { id: "landing", label: "Landing Page", icon: FileText },
  { id: "booking", label: "Booking Wizard", icon: Calendar },
  { id: "portal", label: "Customer Portal", icon: Users },
  { id: "admin", label: "Admin Dashboard", icon: LayoutDashboard },
  { id: "quotes", label: "Quotes", icon: FileText },
  { id: "schedule", label: "Schedule", icon: Calendar },
  { id: "staff", label: "Staff", icon: Users },
  { id: "notifications", label: "Notifications", icon: Bell },
  { id: "auth", label: "Authentication", icon: Users },
];

const TranslationManager = () => {
  const { user, logout } = useAuth();
  const { refreshTranslations } = useLanguage();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const notificationCount = useNotificationCount();
  const [translations, setTranslations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [expandedCategories, setExpandedCategories] = useState(["common", "landing"]);
  const [editedTranslations, setEditedTranslations] = useState({});
  const [hasChanges, setHasChanges] = useState(false);


  useEffect(() => {
    fetchTranslations();
  }, []);

  const fetchTranslations = async () => {
    try {
      const response = await axios.get(`${API}/translations`, { withCredentials: true });
      setTranslations(response.data);
    } catch (error) {
      console.error("Failed to fetch translations:", error);
      toast.error("Failed to load translations");
    } finally {
      setLoading(false);
    }
  };

  const handleTranslationChange = (key, lang, value) => {
    setEditedTranslations(prev => ({
      ...prev,
      [key]: {
        ...(prev[key] || {}),
        [lang]: value
      }
    }));
    setHasChanges(true);
  };

  const saveTranslations = async () => {
    setSaving(true);
    try {
      const updates = Object.entries(editedTranslations).map(([key, values]) => ({
        key,
        ...values
      }));

      await axios.post(`${API}/translations/bulk-update`, updates, { withCredentials: true });
      toast.success("Translations saved successfully");
      setEditedTranslations({});
      setHasChanges(false);
      await fetchTranslations();
      await refreshTranslations();
    } catch (error) {
      console.error("Failed to save translations:", error);
      toast.error("Failed to save translations");
    } finally {
      setSaving(false);
    }
  };

  const resetToDefaults = async () => {
    if (!window.confirm("Are you sure you want to reset all translations to defaults? This cannot be undone.")) {
      return;
    }
    
    try {
      await axios.post(`${API}/translations/reset-defaults`, {}, { withCredentials: true });
      toast.success("Translations reset to defaults");
      setEditedTranslations({});
      setHasChanges(false);
      await fetchTranslations();
      await refreshTranslations();
    } catch (error) {
      console.error("Failed to reset translations:", error);
      toast.error("Failed to reset translations");
    }
  };

  const toggleCategory = (categoryId) => {
    setExpandedCategories(prev => 
      prev.includes(categoryId) 
        ? prev.filter(c => c !== categoryId)
        : [...prev, categoryId]
    );
  };

  const getTranslationValue = (key, lang) => {
    if (editedTranslations[key]?.[lang] !== undefined) {
      return editedTranslations[key][lang];
    }
    const translation = translations.find(t => t.key === key);
    return translation?.[lang] || "";
  };

  const filteredTranslations = translations.filter(t => {
    const matchesSearch = searchQuery === "" || 
      t.key.toLowerCase().includes(searchQuery.toLowerCase()) ||
      t.en.toLowerCase().includes(searchQuery.toLowerCase()) ||
      t.ja.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesCategory = selectedCategory === "all" || t.category === selectedCategory;
    
    return matchesSearch && matchesCategory;
  });

  const groupedTranslations = CATEGORIES.reduce((acc, cat) => {
    acc[cat.id] = filteredTranslations.filter(t => t.category === cat.id);
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex">
      <AdminSidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} notificationCount={notificationCount} />

      {/* Main Content */}
      <main className="flex-1 min-h-screen">
        <header className="sticky top-0 z-30 bg-[#0A0A0A]/80 backdrop-blur-lg border-b border-[#27272A] p-4 lg:p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button 
                className="lg:hidden text-[#A1A1AA] hover:text-white"
                onClick={() => setSidebarOpen(true)}
              >
                <Menu className="w-6 h-6" />
              </button>
              <div>
                <h1 className="font-heading text-2xl lg:text-3xl font-bold text-white uppercase flex items-center gap-3">
                  <Languages className="w-8 h-8 text-[#39FF14]" />
                  Translation Manager
                </h1>
                <p className="text-[#A1A1AA] text-sm">Edit text in English and Japanese</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                onClick={resetToDefaults}
                className="border-[#27272A] text-[#A1A1AA] hover:bg-[#27272A]"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Reset to Defaults
              </Button>
              {hasChanges && (
                <Button
                  onClick={saveTranslations}
                  disabled={saving}
                  className="bg-[#39FF14] text-black hover:bg-[#32D711] font-bold"
                >
                  {saving ? (
                    <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin mr-2" />
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  Save Changes
                </Button>
              )}
            </div>
          </div>
        </header>

        <div className="p-4 lg:p-6">
          {/* Search and Filter */}
          <div className="flex flex-col md:flex-row gap-4 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#A1A1AA]" />
              <Input
                placeholder="Search translations..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-[#121212] border-[#27272A] text-white focus:border-[#39FF14]"
              />
            </div>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="px-4 py-2 bg-[#121212] border border-[#27272A] rounded-lg text-white focus:border-[#39FF14] outline-none"
            >
              <option value="all">All Categories</option>
              {CATEGORIES.map(cat => (
                <option key={cat.id} value={cat.id}>{cat.label}</option>
              ))}
            </select>
          </div>

          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="w-12 h-12 border-4 border-[#39FF14] border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : (
            <div className="space-y-4">
              {CATEGORIES.map(category => {
                const categoryTranslations = groupedTranslations[category.id] || [];
                if (categoryTranslations.length === 0) return null;

                const isExpanded = expandedCategories.includes(category.id);
                const CategoryIcon = category.icon;

                return (
                  <div key={category.id} className="bg-[#121212] border border-[#27272A] rounded-xl overflow-hidden">
                    <button
                      onClick={() => toggleCategory(category.id)}
                      className="w-full flex items-center justify-between p-4 hover:bg-[#1a1a1a] transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <CategoryIcon className="w-5 h-5 text-[#39FF14]" />
                        <span className="font-heading text-lg font-bold text-white uppercase">{category.label}</span>
                        <span className="text-[#A1A1AA] text-sm">({categoryTranslations.length} items)</span>
                      </div>
                      {isExpanded ? (
                        <ChevronDown className="w-5 h-5 text-[#A1A1AA]" />
                      ) : (
                        <ChevronRight className="w-5 h-5 text-[#A1A1AA]" />
                      )}
                    </button>

                    {isExpanded && (
                      <div className="border-t border-[#27272A] divide-y divide-[#27272A]">
                        {categoryTranslations.map(translation => (
                          <div key={translation.key} className="p-4">
                            <div className="mb-3">
                              <Label className="text-[#A1A1AA] text-xs font-mono">{translation.key}</Label>
                            </div>
                            <div className="grid md:grid-cols-2 gap-4">
                              <div>
                                <Label className="text-white text-sm flex items-center gap-2 mb-2">
                                  <span className="w-6 h-4 bg-blue-600 rounded text-[10px] flex items-center justify-center font-bold">EN</span>
                                  English
                                </Label>
                                {translation.en.length > 100 ? (
                                  <Textarea
                                    value={getTranslationValue(translation.key, "en")}
                                    onChange={(e) => handleTranslationChange(translation.key, "en", e.target.value)}
                                    className="bg-[#0A0A0A] border-[#27272A] text-white focus:border-[#39FF14] min-h-[80px]"
                                  />
                                ) : (
                                  <Input
                                    value={getTranslationValue(translation.key, "en")}
                                    onChange={(e) => handleTranslationChange(translation.key, "en", e.target.value)}
                                    className="bg-[#0A0A0A] border-[#27272A] text-white focus:border-[#39FF14]"
                                  />
                                )}
                              </div>
                              <div>
                                <Label className="text-white text-sm flex items-center gap-2 mb-2">
                                  <span className="w-6 h-4 bg-red-600 rounded text-[10px] flex items-center justify-center font-bold">JA</span>
                                  Japanese (日本語)
                                </Label>
                                {translation.ja.length > 100 ? (
                                  <Textarea
                                    value={getTranslationValue(translation.key, "ja")}
                                    onChange={(e) => handleTranslationChange(translation.key, "ja", e.target.value)}
                                    className="bg-[#0A0A0A] border-[#27272A] text-white focus:border-[#39FF14] min-h-[80px]"
                                  />
                                ) : (
                                  <Input
                                    value={getTranslationValue(translation.key, "ja")}
                                    onChange={(e) => handleTranslationChange(translation.key, "ja", e.target.value)}
                                    className="bg-[#0A0A0A] border-[#27272A] text-white focus:border-[#39FF14]"
                                  />
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Help Text */}
          <div className="mt-6 p-4 bg-[#064E3B]/30 border border-[#39FF14]/30 rounded-xl">
            <div className="flex items-start gap-3">
              <Globe className="w-5 h-5 text-[#39FF14] mt-0.5" />
              <div>
                <h3 className="text-[#39FF14] font-semibold mb-1">How Translations Work</h3>
                <ul className="text-[#A1A1AA] text-sm space-y-1">
                  <li>• Edit text in both English and Japanese columns</li>
                  <li>• Click "Save Changes" to apply your edits</li>
                  <li>• Visitors can switch languages using the floating button on the bottom left</li>
                  <li>• Use "Reset to Defaults" to restore original translations</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default TranslationManager;
